import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import ConnectedEngines from "@common/components/connected-engines/connected-engines";
import { isExtensionContext, isWhatsappExtensionContext } from "@common/services/detect-context";
import { useSafeLocation } from "@common/services/utils";
import BoardsPanel from "@common/views/boards-panel/BoardsPanel";
import { Close, Menu } from '@mui/icons-material';
import { IconButton, Stack } from "@mui/material";
import { ReactNode } from "react";
import classes from "./app-view.module.scss";
import { mainSliceActions } from "@common/reducers/MainReducer";
import { useIsMobile } from "@common/hooks/use-is-mobile";
import BotStatusComponent from "../bot-status";
import SubscriptionStatusBar from "../subscription-status-bar";
import LanguageSwitcher from "../language-switcher/language-switcher";
import DunningBanner from "../dunning-banner/dunning-banner";
import NudgeBanner from "../nudge-banner/nudge-banner";
import { TrialUpgradePrompt } from '@common/components/trial-upgrade-prompt/trial-upgrade-prompt';
import ExtensionUpdateBanner from "../extension-update-banner/extension-update-banner";
import MediaTripBanner from "../media-trip-banner/media-trip-banner";
import AgentDay1Prompt from "../agent-day1-prompt/agent-day1-prompt";
import { OnboardingAutoOpen } from "@/pages/onboarding/setup-progress";
import AccountBlocked from "../account-blocked/account-blocked";
import { headerStatusItemsForViewport } from "./header-status-items";
import ClearUserMenu from "../clear-user-menu/clear-user-menu";

interface AppViewProps {
  children: ReactNode;
  title?: string;
  activeExtensionView?: string;
  onClose?: () => void;
  showHeader?: boolean;
  headerActions?: ReactNode;
  /** Small inline element right after the title (e.g. the page's "Watch how" video) —
   *  kept out of headerActions so the status chips stay one uninterrupted set. */
  titleAdornment?: ReactNode;
  showStatusItems?: boolean;
  /**
   * Chromeless mode (the `/clear-*` flow): render only the page content — no
   * sidebar, no banners, no header/status chips and no mobile nav affordance.
   * Folds into the existing showHeader/showStatusItems gates rather than adding
   * a parallel set of conditions.
   */
  bare?: boolean;
}

const AppView = ({ 
  children, 
  title = "", 
  activeExtensionView, 
  onClose, 
  showHeader = true,
  headerActions,
  titleAdornment,
  showStatusItems = true,
  bare = false
}: AppViewProps) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const location = useSafeLocation();
  const renderHeader = showHeader && !bare;
  // At mobile widths the header folds to burger + title + ONE compact status
  // chip (bot-status). The subscription pill and the Connect-WhatsApp /
  // connected-engines control are dropped so the app-bar stops cramming three
  // groups and no longer duplicates the onboarding row's connect CTA. See header-status-items.
  const statusItems = headerStatusItemsForViewport(isMobile, { bare });

  const openMobileNav = () => {
    dispatch(mainSliceActions.toggleMobileMenu(true));
  };

  const mobileNavButton = isMobile ? (
    <IconButton
      onClick={openMobileNav}
      aria-label="open navigation menu"
      // ≥44px tap target on the mobile app-bar (accessible minimum)
      sx={{ flexShrink: 0, minWidth: '44px', minHeight: '44px' }}
    >
      <Menu sx={{ color: 'var(--color-primary)', fontSize: '24px !important' }} />
    </IconButton>
  ) : null;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (isExtensionContext()) {
      dispatch(mainSliceActions.toggleCalendarView(false));
    }
  };

  return (
    <div className={classes.pageContainer}>
      <AccountBlocked />
      {!bare && (
        <BoardsPanel
          activeExtensionView={activeExtensionView}
        />
      )}
      <Stack sx={{
        flex: 1,
        transition: 'all 0.2s',
        minWidth: 0, // shrink to the viewport instead of the widest child (mobile overflow)
      }}>
        {isMobile && !renderHeader && !bare && (
          <IconButton
            onClick={openMobileNav}
            aria-label="open navigation menu"
            sx={{
              position: 'fixed',
              top: 8,
              insetInlineStart: 8,
              zIndex: 1100,
            }}
          >
            <Menu sx={{ color: 'var(--color-primary)', fontSize: '24px !important' }} />
          </IconButton>
        )}
        {!bare && (
          <>
            {/* On web.whatsapp.com the fixed page-level banner already shows; a second in-flow one stacks under it and covers the overlay header. */}
            {!isWhatsappExtensionContext() && <DunningBanner />}
            <ExtensionUpdateBanner />
            <MediaTripBanner />
            <NudgeBanner surface="web" />
            <TrialUpgradePrompt />
          </>
        )}
        {renderHeader && (
          <div className={classes.titleContainer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: '1 1 auto' }}>
              {mobileNavButton}
              <h4 className={classes.pageTitle}>{title}</h4>
              {titleAdornment}
            </div>
            <div className={classes.headerActions}>
              {headerActions}
              {/* <LanguageSwitcher /> */}
              {showStatusItems && (
                <>
                  {statusItems.subscription && <SubscriptionStatusBar compact />}
                  {statusItems.botStatus && <BotStatusComponent dense={isMobile} />}
                  {statusItems.connectedEngines && <ConnectedEngines />}
                </>
              )}
              {isExtensionContext() && (
                <IconButton onClick={handleClose}>
                  <Close sx={{ color: 'var(--color-primary)', fontSize: '24px !important', }} />
                </IconButton>
              )}
            </div>
          </div>
        )}

        {/* The "schedule your first message" onboarding row is calendar-specific — on
            the web it shows only on /calendar (the extension mounts its own copy). */}
        {/* Web: setup lives in the connect wizard — opened once from here, then the sidebar progress. */}
        {!bare && !isExtensionContext() && location.pathname === '/calendar' && <OnboardingAutoOpen />}
        {/* #603 agent-day-1 A/B prompt: web only; renders nothing outside the treatment arm. */}
        {!bare && !isExtensionContext() && <AgentDay1Prompt />}

        <div className={`${classes.contentContainer} ${!renderHeader ? classes.noHeaderContent : ''}`}>
          {children}
        </div>
        {/* With the sidebar hidden there is no other way to see who is signed in
            or to log out. */}
        {bare && !isExtensionContext() && <ClearUserMenu />}
      </Stack>
    </div>
  );
};

export default AppView; 